import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  createManagerList,
  deleteManagerList,
  getManagerLists,
  updateManagerList,
} from '../api/managerListApi'
import type { ManagerList, ManagerListPage, ManagerListUpsert } from '../types'

const KEY = 'manager-lists'

/**
 * Liste Yönetimi sunucu-durumu: liste + oluştur/güncelle/sil. Her başarıda liste invalidate edilir;
 * güncel kayıt ayrıca cache'e yazılır (bayat `modDate` → sahte 409 önlenir).
 *
 * Sorgu anahtarında dil vardır: `groupName` (122) sunucuda isteğin diline göre çözülür.
 */
export function useManagerLists(q?: string) {
  const qc = useQueryClient()
  const { i18n } = useTranslation()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY] })
  }

  const applyUpdated = (updated: ManagerList) =>
    qc.setQueryData<ManagerListPage>([KEY, i18n.language, q ?? ''], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((m) => (m.id === updated.id ? updated : m)) }
        : prev,
    )

  const list = useQuery({
    queryKey: [KEY, i18n.language, q ?? ''],
    queryFn: () => getManagerLists(q),
  })

  // meta.handledCodes: mükerrer ad ekranda ALAN hatası olarak gösterilir → merkezî toast susar.
  const create = useMutation({
    mutationFn: (body: ManagerListUpsert) => createManagerList(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ManagerListUpsert }) =>
      updateManagerList(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteManagerList(id),
    onSuccess: invalidate,
  })

  return { list, create, update, remove }
}
